import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { isMobileChrome, supportsPageVisibility, supportsNetworkInformation } from '@/utils/browserDetection'
import { sseEventManager } from '@/services/sseEventManager'
import { SSEEvent, SSEEventType, SSEEventHandler } from '@/types/sse'

interface SSEContextType {
  connectionStatus: 'disconnected' | 'connecting' | 'connected'
  addEventListener: (eventType: string, handler: SSEEventHandler) => () => void
  forceReconnect: () => void
  enableMobileOptimizations: () => void
  // New global methods
  subscribeToEvent: <T extends SSEEventType>(eventType: T, handler: SSEEventHandler) => () => void
  getConnectionStatus: () => 'disconnected' | 'connecting' | 'connected'
}

const SSEContext = createContext<SSEContextType | undefined>(undefined)

interface SSEProviderProps {
  children: ReactNode
}

export function SSEProvider({ children }: SSEProviderProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected')
  const connectionStatusRef = useRef<'disconnected' | 'connecting' | 'connected'>('disconnected')
  const eventHandlers = useRef<Map<string, Array<SSEEventHandler>>>(new Map())
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const connectFunctionRef = useRef<(() => void) | null>(null)
  const mobileOptimizationsEnabled = useRef(false)
  const lastEventTime = useRef<number>(Date.now())
  const heartbeatCheckInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasHadFirstInteraction = useRef(false)
  const mobileOptimizationsCleanup = useRef<(() => void) | null>(null)
  const reconnectAttempts = useRef<number>(0)

  // Keep connectionStatusRef in sync with connectionStatus state
  useEffect(() => {
    connectionStatusRef.current = connectionStatus
  }, [connectionStatus])

  // Calculate reconnection delay with exponential backoff and jitter
  const getReconnectDelay = React.useCallback(() => {
    // Base delay: higher for mobile Chrome to conserve battery
    const baseDelay = mobileOptimizationsEnabled.current && isMobileChrome() ? 2000 : 1000
    // Maximum delay cap
    const maxDelay = 30000
    // Exponential backoff: baseDelay * 2^attempts, capped at maxDelay
    const exponentialDelay = Math.min(baseDelay * Math.pow(2, reconnectAttempts.current), maxDelay)
    // Add jitter (0-1000ms) to prevent thundering herd
    const jitter = Math.random() * 1000
    return exponentialDelay + jitter
  }, [])

  // Initialize SSE Event Manager with query client
  useEffect(() => {
    sseEventManager.setQueryClient(queryClient)
  }, [queryClient])

  useEffect(() => {
    // Clear any existing reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    // Clean up existing connection
    if (eventSourceRef.current) {
      console.log('Closing existing SSE connection')
      eventSourceRef.current.close()
      eventSourceRef.current = null
      setConnectionStatus('disconnected')
    }

    if (!user?.eventApiKey) {
      console.log('No user or eventApiKey, SSE connection not established')
      return
    }

    const connect = () => {
      // Ensure any existing connection is closed first
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }

      // For mobile Chrome, delay connection until first interaction if optimizations are enabled
      if (mobileOptimizationsEnabled.current && isMobileChrome() && !hasHadFirstInteraction.current) {
        console.log('Mobile Chrome detected: deferring SSE connection until first interaction')
        setConnectionStatus('connecting')
        return
      }

      console.log('Establishing SSE connection for user:', user.id)
      setConnectionStatus('connecting')

      const es = new EventSource(`/events?apiKey=${user.eventApiKey}`)
      eventSourceRef.current = es

      es.onopen = () => {
        setConnectionStatus('connected')
        lastEventTime.current = Date.now()
        // Reset reconnection attempts on successful connection
        reconnectAttempts.current = 0
        console.log('SSE connection established successfully')
      }

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as SSEEvent
          lastEventTime.current = Date.now()
          console.log('SSE event received:', data)

          // Send event to global event manager for automatic query invalidation
          sseEventManager.publish(data)

          // Keep backward compatibility with existing event handlers
          const handlers = eventHandlers.current.get(data.event) || []
          handlers.forEach(handler => handler(data))
        } catch (error) {
          console.error('Error parsing SSE message:', error)
        }
      }

      es.onerror = (error) => {
        console.error('SSE connection error:', error)
        setConnectionStatus('disconnected')

        // Clean up the failed connection
        if (eventSourceRef.current === es) {
          eventSourceRef.current.close()
          eventSourceRef.current = null
        }

        // Only attempt reconnection if we still have a valid user and API key
        if (user?.eventApiKey) {
          // Calculate delay with exponential backoff and jitter
          const delay = getReconnectDelay()
          reconnectAttempts.current += 1
          console.log(`SSE reconnection attempt ${reconnectAttempts.current}, retrying in ${(delay / 1000).toFixed(1)}s...`)
          reconnectTimeoutRef.current = setTimeout(() => {
            if (user?.eventApiKey) { // Double-check before reconnecting
              connect()
            }
          }, delay)
        }
      }
    }

    // Store connect function reference for external calls
    connectFunctionRef.current = connect

    connect()

    // Start heartbeat monitoring for mobile optimizations
    if (mobileOptimizationsEnabled.current) {
      heartbeatCheckInterval.current = setInterval(() => {
        const timeSinceLastEvent = Date.now() - lastEventTime.current
        // Use ref to get current connectionStatus (avoids stale closure)
        if (timeSinceLastEvent > 45000 && connectionStatusRef.current === 'connected') {
          console.log('No SSE events received for 45 seconds, forcing reconnection...')
          connect()
        }
      }, 10000) // Check every 10 seconds
    }

    // Cleanup function
    return () => {
      console.log('SSE useEffect cleanup triggered')

      // Clear intervals and timeouts
      if (heartbeatCheckInterval.current) {
        clearInterval(heartbeatCheckInterval.current)
        heartbeatCheckInterval.current = null
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      // Clean up mobile optimizations event listeners
      if (mobileOptimizationsCleanup.current) {
        mobileOptimizationsCleanup.current()
        mobileOptimizationsCleanup.current = null
      }

      // Close the connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }

      setConnectionStatus('disconnected')
    }
  }, [user?.eventApiKey, user?.id]) // Added user.id to dependencies to ensure proper cleanup/reconnection

  const addEventListener = React.useCallback((eventType: string, handler: SSEEventHandler) => {
    const handlers = eventHandlers.current.get(eventType) || []
    handlers.push(handler)
    eventHandlers.current.set(eventType, handlers)

    return () => {
      const currentHandlers = eventHandlers.current.get(eventType) || []
      const index = currentHandlers.indexOf(handler)
      if (index > -1) {
        currentHandlers.splice(index, 1)
        eventHandlers.current.set(eventType, currentHandlers)
      }
    }
  }, [])

  // Force reconnection function
  const forceReconnect = React.useCallback(() => {
    if (connectFunctionRef.current && user?.eventApiKey) {
      console.log('Force reconnecting SSE...')
      // Reset backoff on manual reconnect
      reconnectAttempts.current = 0
      connectFunctionRef.current()
    }
  }, [user?.eventApiKey])

  // Enable mobile optimizations
  const enableMobileOptimizations = React.useCallback(() => {
    // Prevent multiple initializations
    if (mobileOptimizationsEnabled.current) {
      return
    }

    mobileOptimizationsEnabled.current = true
    console.log('Mobile SSE optimizations enabled')

    // Store cleanup functions for each listener
    const cleanupFunctions: Array<() => void> = []

    // Add first interaction listener for mobile Chrome
    if (isMobileChrome() && !hasHadFirstInteraction.current) {
      const handleFirstInteraction = () => {
        hasHadFirstInteraction.current = true
        console.log('First interaction detected on mobile Chrome, establishing SSE connection')

        // If we're still in deferred connecting state, now actually connect
        // Use ref to get current status (avoids stale closure)
        if (connectionStatusRef.current === 'connecting' && connectFunctionRef.current) {
          connectFunctionRef.current()
        }

        // Remove listeners after first interaction
        document.removeEventListener('touchstart', handleFirstInteraction, { passive: true } as AddEventListenerOptions)
        document.removeEventListener('click', handleFirstInteraction)
        document.removeEventListener('scroll', handleFirstInteraction, { passive: true } as AddEventListenerOptions)
      }

      // Add passive listeners for first interaction
      document.addEventListener('touchstart', handleFirstInteraction, { passive: true } as AddEventListenerOptions)
      document.addEventListener('click', handleFirstInteraction)
      document.addEventListener('scroll', handleFirstInteraction, { passive: true } as AddEventListenerOptions)

      cleanupFunctions.push(() => {
        document.removeEventListener('touchstart', handleFirstInteraction, { passive: true } as AddEventListenerOptions)
        document.removeEventListener('click', handleFirstInteraction)
        document.removeEventListener('scroll', handleFirstInteraction, { passive: true } as AddEventListenerOptions)
      })
    }

    // Add page visibility change listener
    if (supportsPageVisibility()) {
      const handleVisibilityChange = () => {
        // Use ref to get current status (avoids stale closure)
        if (document.visibilityState === 'visible' && connectionStatusRef.current !== 'connected') {
          console.log('Page became visible, attempting SSE reconnection...')
          setTimeout(() => {
            if (connectFunctionRef.current) {
              connectFunctionRef.current()
            }
          }, 500) // Small delay to ensure page is fully active
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)
      cleanupFunctions.push(() => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      })
    }

    // Add focus event listener
    const handleFocus = () => {
      // Use ref to get current status (avoids stale closure)
      if (connectionStatusRef.current !== 'connected') {
        console.log('Window gained focus, ensuring SSE connection...')
        setTimeout(() => {
          if (connectFunctionRef.current) {
            connectFunctionRef.current()
          }
        }, 300)
      }
    }

    window.addEventListener('focus', handleFocus)
    cleanupFunctions.push(() => {
      window.removeEventListener('focus', handleFocus)
    })

    // Add network change listener if supported
    if (supportsNetworkInformation()) {
      const handleNetworkChange = () => {
        console.log('Network connection changed, reconnecting SSE...')
        setTimeout(() => {
          if (connectFunctionRef.current) {
            connectFunctionRef.current()
          }
        }, 1000) // Delay to allow network to stabilize
      }

      // @ts-ignore - TypeScript doesn't have types for navigator.connection yet
      navigator.connection?.addEventListener('change', handleNetworkChange)
      cleanupFunctions.push(() => {
        // @ts-ignore - TypeScript doesn't have types for navigator.connection yet
        navigator.connection?.removeEventListener('change', handleNetworkChange)
      })
    }

    // Store the combined cleanup function
    mobileOptimizationsCleanup.current = () => {
      console.log('Cleaning up mobile SSE optimizations')
      cleanupFunctions.forEach(cleanup => cleanup())
      mobileOptimizationsEnabled.current = false
    }
  }, []) // No dependencies needed - uses refs for all mutable state

  // Global event subscription method
  const subscribeToEvent = React.useCallback(<T extends SSEEventType>(
    eventType: T,
    handler: SSEEventHandler
  ) => {
    return sseEventManager.subscribe(eventType, handler)
  }, [])

  // Get connection status method
  const getConnectionStatus = React.useCallback(() => {
    return connectionStatus
  }, [connectionStatus])

  const value: SSEContextType = {
    connectionStatus,
    addEventListener,
    forceReconnect,
    enableMobileOptimizations,
    subscribeToEvent,
    getConnectionStatus
  }

  return (
    <SSEContext.Provider value={value}>
      {children}
    </SSEContext.Provider>
  )
}

export function useSSE() {
  const context = useContext(SSEContext)
  if (context === undefined) {
    throw new Error('useSSE must be used within a SSEProvider')
  }
  return context
} 